import { format } from 'date-fns';
import { AutocompleteInput } from '../../components/admin/autocomplete-input';
import { DateInput } from '../../components/admin/date-input';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { ReferenceInput } from '../../components/admin/reference-input';
import { SelectInput } from '../../components/admin/select-input';
import { TextInput } from '../../components/admin/text-input';
import {
  required,
  useDataProvider,
  useGetList,
  useNotify,
  useRedirect,
  useTranslate,
} from 'ra-core';
import { type AdminDataProvider } from '../../api/adminDataProvider';
import { MoonstarCreate } from '../Moonstar/MoonstarCreate';
import { MoonstarCreateForm } from '../Moonstar/MoonstarCreateForm';
import { SimpleStack } from '../Moonstar/SimpleStack';
import { phoneNumber } from '../../utils/validation';
import { getErrorMessage } from './getErrorMessage';
import { useRestrictedUserUnits } from './useRestrictedUserUnits';
import { useHasPermission } from '../../auth/useHasPermission';
import { useCurrentUser } from '../../auth/currentUserContext';
import { getAdminUserDisplayName } from './types';
import { parseDate } from './UserEdit';

export const UserCreate = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const { data: unitChoices = [] } = useGetList<{ id: number; name: string }>(
    'units',
    {
      pagination: { page: 1, perPage: 999 },
      sort: { field: 'name', order: 'ASC' },
    }
  );
  const shouldBeOptional = useHasPermission(
    'OPTIONAL_USER_ONBOARDING_FIELDS_ACCESS'
  );
  const notify = useNotify();
  const redirect = useRedirect();
  const { units } = useCurrentUser();
  const { isUnitAdmin, isPending, isError, error, refetch } =
    useRestrictedUserUnits();

  if (isUnitAdmin) {
    if (isPending) return <Loading />;
    if (isError) {
      return (
        <ErrorPage error={error} resetErrorBoundary={() => void refetch()} />
      );
    }
  }

  const validateUnique = async (value: string, field: 'phone' | 'internal') => {
    if (!value) return undefined;
    try {
      const user =
        field === 'phone'
          ? await dataProvider.getUserByPhoneNumber(value)
          : await dataProvider.getUserByInternalId(value);
      return user
        ? `This ${field === 'phone' ? 'phone number' : 'internal identifier'} is already taken by user: ${getAdminUserDisplayName(user)}`
        : undefined;
    } catch (validationError) {
      return getErrorMessage(validationError);
    }
  };

  const getName = async (
    resource: 'departments' | 'jobTitles' | 'regions',
    id: unknown
  ) => {
    if (id === undefined || id === null || id === '') return undefined;
    const response = await dataProvider.getOne(resource, { id });
    return response.data.name as string;
  };

  const save = async (data: Record<string, unknown>) => {
    try {
      const [department, jobTitle, region] = await Promise.all([
        getName('departments', data.department),
        getName('jobTitles', data.jobTitle),
        getName('regions', data.region),
      ]);

      await dataProvider.postUser({
        ...data,
        department,
        jobTitle,
        region,
        ...(isUnitAdmin ? { unit: units[0] } : {}),
        ...(data.startDate
          ? {
              startDate: format(
                new Date(data.startDate as string),
                'dd-MM-yyyy'
              ),
            }
          : {}),
      });
      notify('User created', { type: 'info' });
      redirect('list', 'users');
    } catch (saveError) {
      notify(getErrorMessage(saveError), { type: 'error' });
    }
  };

  return (
    <MoonstarCreate redirect="list">
      <MoonstarCreateForm
        onSubmit={save}
        defaultValues={isUnitAdmin ? { unit: units[0] } : undefined}
      >
        <div className="flex flex-wrap gap-4">
          <SimpleStack>
            <h2 className="text-lg font-semibold">
              {translate('moonstar.user.edit.section.personalDetails')}
            </h2>
            <TextInput
              source="firstName"
              validate={shouldBeOptional ? undefined : required()}
            />
            <TextInput
              source="lastName"
              validate={shouldBeOptional ? undefined : required()}
            />
            <TextInput
              source="phoneNumber"
              validate={[
                required(),
                phoneNumber(),
                async (value) => validateUnique(value, 'phone'),
              ]}
            />
            <TextInput source="email" type="email" />
          </SimpleStack>
          <SimpleStack>
            <h2 className="text-lg font-semibold">
              {translate('moonstar.user.edit.section.companyRelatedInfo')}
            </h2>
            <TextInput
              source="internalId"
              label="Internal ID"
              parse={(value) => (value as string | null) ?? undefined}
              validate={async (value) => validateUnique(value, 'internal')}
            />
            <ReferenceInput
              source="jobTitle"
              reference="jobTitles"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                optionValue="id"
                validate={shouldBeOptional ? undefined : required()}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <ReferenceInput
              source="department"
              reference="departments"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                optionValue="id"
                validate={shouldBeOptional ? undefined : required()}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <ReferenceInput
              source="region"
              reference="regions"
              perPage={999}
              sort={{ field: 'name', order: 'ASC' }}
            >
              <AutocompleteInput
                optionText="name"
                optionValue="id"
                validate={shouldBeOptional ? undefined : required()}
                filterToQuery={(query: string) => ({ name: query })}
              />
            </ReferenceInput>
            <AutocompleteInput
              source="unit"
              choices={unitChoices}
              optionText="name"
              optionValue="name"
              validate={required()}
              disabled={isUnitAdmin}
            />
            <DateInput
              source="startDate"
              validate={required()}
              parse={parseDate}
            />
            <SelectInput
              source="languageCountryCode"
              choices={[
                { id: 'en', name: 'English' },
                { id: 'ro', name: 'Romanian' },
              ]}
              defaultValue="en"
              validate={required()}
            />
          </SimpleStack>
        </div>
      </MoonstarCreateForm>
    </MoonstarCreate>
  );
};
